import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd';
import { AccountFormFields } from 'constants/account/account-form-fields';
import { Pattern } from 'constants/validation';
import { IFormField } from 'interfaces';

type FieldNameType = string | string[];

export const useReturnFormFields = (
  form: FormInstance,
  fieldNames: FieldNameType[],
  isForLogin?: boolean,
  defaultValues?: Record<string, any>
) => {
  const { t } = useTranslation();

  const formFields: IFormField[] = useMemo(
    () => [
      {
        name: AccountFormFields.ArtistName,
        rules: [
          {
            required: true,
            message: t('account.pleaseInputYourName')
          }
        ],
        label: t('account.artistName'),
        tooltip: t('account.artistTooltip'),
        defaultValue: defaultValues?.[AccountFormFields.ArtistName]
      },
      {
        name: AccountFormFields.UserName,
        rules: [
          {
            required: true,
            message: t('account.pleaseInputUserName')
          },
          {
            pattern: Pattern.username,
            message: t('account.pleaseInputCorrectUserName')
          }
        ],
        label: t('account.username'),
        tooltip: t('account.usernameTooltip'),
        defaultValue: defaultValues?.[AccountFormFields.UserName]
      },
      {
        name: AccountFormFields.DisplayName,
        rules: [
          {
            required: true,
            message: t('account.pleaseEnterDisplayName')
          }
        ],
        label: t('account.displayName'),
        tooltip: t('account.displayNameTooltip'),
        defaultValue: defaultValues?.[AccountFormFields.DisplayName]
      },
      {
        name: AccountFormFields.Email,
        rules: [
          {
            required: true,
            message: t('account.pleaseInputYourEmail'),
            type: 'email'
          }
        ],
        label: t('account.email'),
        defaultValue: defaultValues?.[AccountFormFields.Email]
      },
      {
        name: AccountFormFields.Phone,
        rules: [
          {
            required: true,
            message: t('account.pleaseInputYourPhone')
          },
          {
            pattern: Pattern.phone,
            message: t('account.pleaseEnterOnlyNumber')
          }
        ],
        label: t('account.phone'),
        defaultValue: defaultValues?.[AccountFormFields.Phone]
      },
      {
        name: AccountFormFields.Occupation,
        rules: [],
        label: t('account.occupation'),
        tooltip: t('account.occupationTooltip'),
        defaultValue: defaultValues?.[AccountFormFields.Occupation]
      },
      {
        name: AccountFormFields.Password,
        rules: [
          {
            required: true,
            message: t('account.pleaseInputYourPassword')
          },
          ...(!isForLogin
            ? [
                {
                  pattern: Pattern.password,
                  message: t('account.passwordMustContain')
                }
              ]
            : [])
        ],
        label: t('account.password'),
        type: 'password',
        defaultValue: defaultValues?.[AccountFormFields.Password]
      },
      {
        name: AccountFormFields.PasswordConfirmation,
        rules: [
          {
            required: true,
            message: t('account.pleaseInputConfirmPassword')
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(t('account.passwordConfirmDoesNotMatch'))
              );
            }
          })
        ],
        label: t('account.confirmPassword'),
        type: 'password',
        defaultValue: defaultValues?.[AccountFormFields.PasswordConfirmation]
      },
      {
        name: AccountFormFields.BankAccount,
        rules: [
          {
            required: true,
            message: t('account.pleaseEnterBankAccountDetails')
          }
        ],
        label: t('account.bankAccount'),
        tooltip: t('account.bankAccountTooltip'),
        defaultValue: defaultValues?.[AccountFormFields.BankAccount]
      },
      {
        name: AccountFormFields.ContactName,
        rules: [
          {
            required: true,
            message: t('account.pleaseEnterContactName')
          }
        ],
        label: t('account.contactName'),
        tooltip: t('account.contactNameTooltip'),
        defaultValue: defaultValues?.[AccountFormFields.ContactName]
      },
      {
        name: AccountFormFields.ContactEmail,
        rules: [
          {
            required: true,
            message: t('account.pleaseInputYourEmail'),
            type: 'email'
          }
        ],
        label: t('account.contactEmail'),
        tooltip: t('account.contactEmailTooltip'),
        defaultValue: defaultValues?.[AccountFormFields.ContactEmail]
      },
      {
        name: AccountFormFields.ContactPhone,
        rules: [
          {
            pattern: new RegExp(/(^[0-9]+$)/),
            required: true,
            message: t('account.pleaseEnterContactPhone')
          }
        ],
        label: t('account.contactPhone'),
        tooltip: t('account.contactPhoneTooltip'),
        defaultValue: defaultValues?.[AccountFormFields.ContactPhone]
      },
      {
        name: AccountFormFields.CeoAddress,
        rules: [],
        label: t('account.ceoAddress'),
        defaultValue: defaultValues?.[AccountFormFields.CeoAddress]
      },
      {
        name: AccountFormFields.CeoCity,
        rules: [],
        label: t('account.city'),
        defaultValue: defaultValues?.[AccountFormFields.CeoCity]
      },
      {
        name: AccountFormFields.CeoState,
        rules: [],
        label: t('account.ceoState'),
        defaultValue: defaultValues?.[AccountFormFields.CeoState]
      },
      {
        name: AccountFormFields.CeoPostCode,
        rules: [],
        label: t('account.zipCode'),
        defaultValue: defaultValues?.[AccountFormFields.CeoPostCode]
      },
      {
        name: AccountFormFields.CeoCountry,
        rules: [],
        label: t('account.country'),
        defaultValue: defaultValues?.[AccountFormFields.CeoCountry]
      },
      {
        name: AccountFormFields.RegisterGoal,
        rules: [
          {
            required: true,
            message: t('account.pleaseEnterRegisterGoal')
          }
        ],
        label: t('account.registerGoal'),
        defaultValue: defaultValues?.[AccountFormFields.RegisterGoal]
      },
      {
        name: AccountFormFields.TermsAndConditions,
        rules: [
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.reject(
                  t('account.requiredAgreeTermsAndConditions')
                );
              }
              return Promise.resolve();
            }
          }
        ],
        label: t('account.termsAndConditions'),
        defaultValue: defaultValues?.[AccountFormFields.TermsAndConditions]
      },
      {
        name: AccountFormFields.PromotionalEmails,
        rules: [],
        label: t('account.promotionalEmails'),
        defaultValue: defaultValues?.[AccountFormFields.PromotionalEmails]
      }
    ],
    []
  );

  return fieldNames.map(field => {
    if (Array.isArray(field)) {
      const foundFields = formFields.filter(f => field.includes(f.name));
      return foundFields;
    }
    return formFields.find(f => f.name === field) || null;
  });
};
